import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';

import { Button } from '@/design-system/atoms/Button';
import { PlacesTypeahead } from '@/design-system/atoms/Forms/PlacesTypeahead';
import { TextField } from '@/design-system/atoms/Forms/TextField';
import { Typeahead } from '@/design-system/atoms/Forms/Typeahead';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { Title } from '@/design-system/atoms/Title';
import IllustrationPlayer from '@/design-system/molecules/IllustrationPlayer';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { trackInteraction } from '@/utils/analytics';
import { locationCheck } from '@/utils/locationCheck';

import { useLocationUIContext } from '../../../contexts/LocationUIContext';
import { formValidation } from '../../../utils/formValidation';
import styles from '../LocationHero.module.scss';

const LocationHeroDefault = ({
    inputNameTypeahead,
    inputNameZipCity,
    illustration,
    hideMobileIllustration,
    searchValue,
    setSearchValue,
    onSelectionItemChange,
    onSelectionZipCityChange,
    typeaheadItems,
    typeaheadFixedItem,
    zipCityValue,
    setZipCityValue,
    onSubmit,
    submitDisabled,
    googleMaps,
    placeSuggestions,
    handleZipCityInputChange,
    zipInvalid,
    setZipInvalid,
    handleAutoSelect,
}) => {
    const breakpoint = useBreakpoint();

    const { title, searchLabel, searchErrorMessage, zipLabel, zipErrorMessage } =
        useLocationUIContext();

    const searchRef = useRef();
    const zipRef = useRef();
    const [searchValueInvalid, setSearchValueInvalid] = useState(false);
    const [searchValueError, setSearchValueError] = useState();
    const [zipError, setZipError] = useState();

    const submitForm = useCallback(
        async (e) => {
            e.preventDefault();

            const { isGeocoded, newLocation } = await locationCheck({
                zipCityValue,
                suggestions: placeSuggestions,
            });

            const [isValid, keys, message] = formValidation(
                {
                    searchValue,
                    zipCityValue: newLocation || zipCityValue,
                    isGeocoded: isGeocoded,
                },
                { searchValue: searchErrorMessage, zipValue: zipErrorMessage },
            );

            if (isValid) {
                setSearchValueInvalid(false);
                setZipInvalid(false);
                onSubmit({ term: searchValue, zipCityValue: newLocation || zipCityValue });

                //analytics
                trackInteraction({
                    eventName: 'search',
                    componentName: 'Location Hero',
                    interactionType: 'search_results',
                    componentTitle: title,
                    selector: 'search',
                    searchTerm: searchValue,
                    zipCode: !isNaN(zipCityValue) ? zipCityValue : '',
                });

                return;
            }

            if (keys.includes('zipCityValue') && keys.includes('searchValue')) {
                setZipInvalid(true);
                setSearchValueInvalid(true);
                setZipError(message);
                setSearchValueError(message);
                searchRef.current.focus();
            } else if (keys.includes('zipCityValue')) {
                setSearchValueInvalid(false);
                setZipInvalid(true);
                setZipError(message);
                zipRef.current.focus();
            }
        },
        [
            onSubmit,
            placeSuggestions,
            searchValue,
            searchErrorMessage,
            title,
            zipCityValue,
            zipErrorMessage,
            setZipInvalid,
        ],
    );

    return (
        <>
            <Grid
                className={classnames(
                    styles[`location-hero__grid`],
                    hideMobileIllustration &&
                        !breakpoint?.medium &&
                        styles['location-hero__grid--hidden-illustration'],
                )}
            >
                <GridColumn
                    colSpan={{ sm: 4, md: 6, lg: 4 }}
                    className={styles[`location-hero__content`]}
                >
                    <Title
                        className={styles[`location-hero__title`]}
                        variant={Title.VARIANT.H2}
                        tag={Title.TAG.H1}
                        content={title}
                    />
                    <form
                        className={styles[`location-hero__form`]}
                        onSubmit={submitForm}
                        data-trigger="search"
                    >
                        <Typeahead
                            ref={searchRef}
                            name={inputNameTypeahead}
                            label={searchLabel}
                            variant={TextField.VARIANT.BOTTOM_BORDER}
                            buttonProps={undefined}
                            itemLineClamp={4}
                            items={typeaheadItems}
                            fixedItem={typeaheadFixedItem}
                            className={styles[`location-hero__typeahead`]}
                            inputValue={searchValue}
                            onInputChange={setSearchValue}
                            onSelectionItemChange={onSelectionItemChange}
                            isInvalid={searchValueInvalid}
                            validationErrors={searchValueError}
                            srError
                        />
                        <PlacesTypeahead
                            ref={zipRef}
                            label={zipLabel}
                            inputNameTypeahead={inputNameZipCity}
                            inputValue={zipCityValue}
                            onInputChange={handleZipCityInputChange}
                            onSelectionItemChange={onSelectionZipCityChange}
                            onGeolocationSuccess={setZipCityValue}
                            isInvalid={zipInvalid}
                            validationErrors={zipError}
                            className={styles[`location-hero__typeahead`]}
                            googleMaps={googleMaps}
                            autoSelect={{ enabled: true, callback: handleAutoSelect }}
                        />

                        <div className={styles[`location-hero__button-wrapper`]}>
                            <Button
                                label="Search"
                                ariaLabel="Submit Location Search"
                                buttonStyle={Button.STYLE.PRIMARY}
                                className={styles[`location-hero__button`]}
                                type="submit"
                                disabled={submitDisabled}
                                data-submit-type="location-search"
                            />
                        </div>
                    </form>
                </GridColumn>
                {hideMobileIllustration && !breakpoint?.medium ? null : (
                    <GridColumn
                        colSpan={{ sm: 4, md: 6, lg: 8 }}
                        className={styles[`location-hero__illustration`]}
                    >
                        {illustration?.scene && illustration?.title && (
                            <IllustrationPlayer {...illustration} />
                        )}
                    </GridColumn>
                )}
            </Grid>
        </>
    );
};

LocationHeroDefault.propTypes = {
    /**
     * The title of the location hero component.
     */
    title: PropTypes.string,
    /**
     * The label for the search input.
     */
    searchLabel: PropTypes.string,
    /**
     * The label for the zip input.
     */
    zipLabel: PropTypes.string,
    /**
     * The illustration to display.
     */
    illustration: PropTypes.object,
};

export default LocationHeroDefault;
